import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import ReCAPTCHA from 'react-google-recaptcha';
import { PasswordInput } from '../../../shared/components/common/password-input/PasswordInput';
import { LOGIN } from '../../../shared/routes/path';
import { REACT_APP_RECAPTCHA_SITE_KEY } from '../../../shared/utils/config';
import { PRIVACY_POLICY, TERMS_OF_USE } from '../../../shared/utils/urls';
import useSignupSubmit from '../hooks/UseSignupSubmit';
import HoneypotField from '../../../shared/components/common/honeypot-field/HoneypotField';
import './Brand24SignupPage.scss';

/**
 * Znacznik wejścia rejestracyjnego. Backend rozwija go na `register_source = 'SIGNUP_PAGE_BRAND24'`
 * (`resolveSignupOrigin` w llm-common) — i tylko na to. `client_type` zostaje pusty, więc konto NIE
 * dostaje tagów `brand24_clients` / `B24_Client_Masterclass` ani dedykowanego opiekuna B24; w
 * Intercomie rozpoznaje je `api_client_name = 'b24_signup'`. Wartość musi się zgadzać z kluczem
 * w `SIGNUP_ORIGINS`.
 */
const SIGNUP_ORIGIN_BRAND24 = 'brand24';

/** Trzy obietnice pod formularzem — kolejność jak w projekcie. */
const BENEFIT_KEYS = ['b24Signup.benefit1', 'b24Signup.benefit2', 'b24Signup.benefit3'];

/** Logotypy klientów w stopce — pliki SVG, każdy z własną szerokością wynikającą z proporcji. */
const CUSTOMER_LOGOS = [
  { name: 'McCann', file: 'mccann', width: 122 },
  { name: 'Uber', file: 'uber', width: 78 },
  { name: 'Stanford University', file: 'stanford', width: 83 },
  { name: 'Samsung', file: 'samsung', width: 98 },
  { name: 'Carlsberg', file: 'carlsberg', width: 81 },
  { name: 'Wondery', file: 'wondery', width: 138 },
];

/**
 * Rejestracja dedykowana ruchowi z Brand24 — osobny adres, żeby dało się ją linkować i mierzyć
 * niezależnie od zwykłego `/signup`. Logika rejestracji jest wspólna (`useSignupSubmit`); różni je
 * wyłącznie układ: tu jedna wyśrodkowana kolumna z lockupem „chatbeat by BRAND24" zamiast dwóch
 * kolumn z testimonialami.
 */
const Brand24SignupPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ email: string; password: string; contactReason: string }>();

  const {
    recaptchaRef,
    submit,
    isSubmitting,
    apiError,
    clearApiError,
    verificationSent,
    resendCooldown,
    resendSuccess,
    handleResend,
  } = useSignupSubmit(SIGNUP_ORIGIN_BRAND24);

  useEffect(() => {
    document.body.classList.add('visible', 'white');
    return () => document.body.classList.remove('visible', 'white');
  }, []);

  /** Pasek pełnej szerokości — dlatego siedzi obok `.b24-signup`, poza jego paddingiem. */
  const banner = (
    <div className="b24-signup__banner">
      <span className="ico--shield-check ico--sm b24-signup__banner-icon" aria-hidden="true" />
      <p className="b24-signup__banner-text">
        {t('b24Signup.bannerPrefix')} <strong>Brand24</strong> {t('b24Signup.bannerSuffix')}
      </p>
      <span className="b24-signup__banner-divider" aria-hidden="true" />
      <a className="b24-signup__banner-back" href="https://brand24.com">
        <span className="ico--arrow-left ico--sm b24-signup__banner-icon" aria-hidden="true" />
        {t('b24Signup.bannerBack')}
      </a>
    </div>
  );

  const header = (
    <header className="b24-signup__header">
      <div className="b24-signup__lockup">
        <div className="logo-box b24-signup__logo" />
        <p className="b24-signup__lockup-by">
          {t('b24Signup.by')}
          {/* Znak i wordmark Brand24 trzymają się bliżej siebie niż podpis — stąd grupa
              z własnym odstępem zamiast wspólnego `gap` rodzica. */}
          <span className="b24-signup__lockup-brand-group">
            <img
              className="b24-signup__lockup-mark"
              src={require('../../../assets/images/b24-fav.png')}
              alt=""
            />
            <span className="b24-signup__lockup-brand" role="img" aria-label="Brand24" />
          </span>
        </p>
      </div>

      <h1 className="b24-signup__title">{t('b24Signup.title')}</h1>
      <p className="b24-signup__subtitle">{t('b24Signup.subtitle')}</p>
    </header>
  );

  if (verificationSent) {
    return (
      <div className="b24-signup-page">
        {banner}
        <div className="b24-signup">
          {header}
          <div className="b24-signup__card">
            <h2 className="b24-signup__verify-title">{t('checkYourEmail')}</h2>
            <p className="b24-signup__note">{t('verificationEmailSent')}</p>
            {resendSuccess && <p className="b24-signup__note">{t('resendVerificationSuccess')}</p>}
            <Button
              className="btn btn--link"
              type="button"
              disabled={resendCooldown > 0}
              onClick={handleResend}
              label={
                resendCooldown > 0
                  ? `${t('resendVerification')} (${Math.floor(resendCooldown / 60)}:${String(
                      resendCooldown % 60
                    ).padStart(2, '0')})`
                  : t('resendVerification')
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="b24-signup-page">
      {banner}
      <div className="b24-signup">
        {header}

        {/* `form-box form-box--auth` — te same style pól i komunikatów walidacji, co na `/signup`. */}
        <form
          className="b24-signup__card form-box form-box--auth"
          onSubmit={handleSubmit(({ email, password, contactReason }) =>
            submit(email, password, contactReason)
          )}
        >
          <HoneypotField registration={register('contactReason')} />
          <div className="form-box__row">
            <InputText
              onInput={clearApiError}
              placeholder={t('b24Signup.emailPlaceholder')}
              className={errors.email || apiError ? 'form-box__invalid' : ''}
              {...register('email', {
                required: t('fieldIsRequired'),
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: t('wrongEmailAddress'),
                },
              })}
            />
            {errors.email && <span>{errors.email.message}</span>}
          </div>

          <div className="form-box__row">
            <PasswordInput
              onInput={clearApiError}
              placeholder={t('b24Signup.passwordPlaceholder')}
              className={errors.password || apiError ? 'form-box__invalid' : ''}
              {...register('password', { required: t('fieldIsRequired') })}
            />
            {errors.password && <span>{errors.password.message}</span>}
            {!errors.password && apiError && <span>{apiError}</span>}
          </div>

          <p className="b24-signup__hint">{t('b24Signup.passwordHint')}</p>

          <button type="submit" className="b24-signup__submit" disabled={isSubmitting}>
            {t('b24Signup.submit')}
          </button>

          <p className="b24-signup__note">{t('b24Signup.disclaimer')}</p>

          <p className="b24-signup__note">
            {t('b24Signup.legalPrefix')}{' '}
            <a href={TERMS_OF_USE} target="_blank" rel="noopener noreferrer">
              {t('termsAndConditions')}
            </a>{' '}
            {t('b24Signup.legalAnd')}{' '}
            <a href={PRIVACY_POLICY} target="_blank" rel="noopener noreferrer">
              {t('privacyPolicy')}
            </a>
          </p>
        </form>

        <p className="b24-signup__login">
          {t('b24Signup.haveAccount')} <Link to={LOGIN}>{t('b24Signup.logIn')}</Link>
        </p>

        <div className="b24-signup__benefits">
          {BENEFIT_KEYS.map((key) => (
            <div key={key} className="b24-signup__benefit">
              <span className="ico--circle-check ico--sm b24-signup__benefit-icon" aria-hidden="true" />
              <span>{t(key)}</span>
            </div>
          ))}
        </div>

        <div className="b24-signup__customers">
          {CUSTOMER_LOGOS.map((logo) => (
            <img
              key={logo.file}
              className="b24-signup__customer"
              src={require(`../../../assets/images/${logo.file}.svg`)}
              style={{ width: logo.width }}
              alt={logo.name}
            />
          ))}
        </div>

        <footer className="b24-signup__footer">
          <p className="b24-signup__footer-text">
            <strong>Chatbeat</strong> {t('b24Signup.footerProductOf')}{' '}
            <strong>Brand24 Global Inc.</strong> {t('b24Signup.footerSameCompany')}
          </p>
          <p className="b24-signup__footer-links">
            <a href="mailto:help@brand24.com">help@brand24.com</a>
            <span aria-hidden="true">·</span>
            <a href="https://brand24.com" target="_blank" rel="noopener noreferrer">
              brand24.com
            </a>
          </p>
        </footer>

        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={REACT_APP_RECAPTCHA_SITE_KEY || 'sample'}
          size="invisible"
        />
      </div>
    </div>
  );
};

export default Brand24SignupPage;
